import { useState } from 'react';
import { usePostalLookup } from '../../hooks/usePostalLookup';
import { useDuplicateCustomer } from '../../hooks/useDuplicateCustomer';
import { customerSchema } from '../../schemas/customerSchema';
import { PrimaryAddressBlock, InstallationAddressBlock } from './CustomerAddressBlocks';
import { useQuoteStore, type CustomerInfo } from '../../store/useQuoteStore';

type FieldErrors = Partial<Record<keyof CustomerInfo, string>>;

export default function CustomerForm() {
  const customer = useQuoteStore((state) => state.customer);
  const updateCustomerField = useQuoteStore((state) => state.updateCustomerField);
  const issuedCompany = useQuoteStore((state) => state.issuedCompany);
  const setIssuedCompany = useQuoteStore((state) => state.setIssuedCompany);

  // Field-level errors are populated on blur and cleared as the user types.
  // The full safeParse at submit time is owned by useQuoteCheckout — the
  // shape stored here is only for inline UX feedback.
  const [errors, setErrors] = useState<FieldErrors>({});

  const handleChange = (field: keyof CustomerInfo, value: string | boolean) => {
    updateCustomerField(field, value);
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleBlur = (field: keyof CustomerInfo) => {
    // safeParse the whole customer object and surface only the issue (if any)
    // for the blurred field. Cheap enough at this object size — avoids
    // maintaining a parallel per-field validator schema.
    const result = customerSchema.safeParse(customer);
    if (result.success) {
      if (errors[field]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
      return;
    }
    const fieldIssue = result.error.issues.find((i) => i.path[0] === field);
    setErrors((prev) => {
      const next = { ...prev };
      if (fieldIssue) {
        next[field] = fieldIssue.message;
      } else {
        delete next[field];
      }
      return next;
    });
  };

  const { status: postalStatus, isSearching } = usePostalLookup(
    customer.postal,
    true,
    (address) => {
      updateCustomerField('blk', address.BLK_NO);
      updateCustomerField('street', address.ROAD_NAME);
      updateCustomerField('building', address.BUILDING !== 'NIL' ? address.BUILDING : '');
    },
  );

  const hasDifferentInstall = !!customer.hasDifferentInstall;
  const { status: installPostalStatus, isSearching: isSearchingInstall } = usePostalLookup(
    customer.installPostal,
    hasDifferentInstall,
    (address) => {
      updateCustomerField('installBlk', address.BLK_NO);
      updateCustomerField('installStreet', address.ROAD_NAME);
      updateCustomerField('installBuilding', address.BUILDING !== 'NIL' ? address.BUILDING : '');
    },
  );

  const { duplicateMatch, dismissMatch, clearMatch } = useDuplicateCustomer(
    customer.mobile,
    customer.name,
  );

  const applyDuplicateMatch = () => {
    if (!duplicateMatch) return;
    const m = duplicateMatch;
    updateCustomerField('name', m.name);
    updateCustomerField('mobile', m.mobile);
    if (m.email) updateCustomerField('email', m.email);
    if (m.primaryAddress) {
      updateCustomerField('postal', m.primaryAddress.postal ?? '');
      updateCustomerField('blk', m.primaryAddress.blk ?? '');
      updateCustomerField('street', m.primaryAddress.street ?? '');
      updateCustomerField('building', m.primaryAddress.building ?? '');
      updateCustomerField('unit', m.primaryAddress.unit ?? '');
    }
    // The install address is deliberately NOT prefilled from the customer
    // record: it lives on the order, not the customer master (Ben,
    // 2026-09-02), because one customer can have many sites. The rep enters
    // it per quote, and it is the QUOTE's install fields that flow into the
    // order snapshot. Leaving the current quote's entry untouched here is the
    // safe behaviour — the alternative, clearing it, would silently discard
    // an install address the rep had already typed for THIS job.
    setErrors({});
    clearMatch();
  };

  return (
    <section className="bg-white border border-outline-variant rounded-xl p-8 shadow-sm">
      <div className="flex justify-between items-start mb-6 pb-4 border-b border-outline-variant">
        <div>
          <h2 className="text-xl font-bold font-headline text-secondary mb-1">
            Customer Information
          </h2>
          <p className="text-outline font-medium text-xs uppercase tracking-widest">
            Client Details for Quote
          </p>
        </div>
      </div>

      <form className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
        {duplicateMatch && (
          <div className="md:col-span-2 flex items-start justify-between gap-4 p-4 rounded-lg border border-amber-300 bg-amber-50">
            <div className="flex items-start gap-3 min-w-0">
              <span className="material-symbols-outlined text-amber-700 text-[20px] mt-0.5">
                contact_page
              </span>
              <div className="min-w-0">
                <p className="text-sm font-bold text-amber-900">
                  Customer already on file: {duplicateMatch.name}
                </p>
                <p className="text-xs text-amber-800 mt-0.5 truncate">
                  Mobile {duplicateMatch.mobile}
                  {duplicateMatch.email ? ` · ${duplicateMatch.email}` : ''}
                  {duplicateMatch.primaryAddress?.postal
                    ? ` · ${duplicateMatch.primaryAddress.postal}`
                    : ''}
                </p>
                <p className="text-[11px] text-amber-700 mt-1">
                  Apply this record so the new quote attaches to the existing customer instead of
                  creating a duplicate.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={applyDuplicateMatch}
                className="text-xs font-bold uppercase tracking-wider bg-amber-600 hover:bg-amber-700 text-white px-3 py-2 rounded-md transition-colors"
              >
                Use this customer
              </button>
              <button
                type="button"
                onClick={dismissMatch}
                className="text-xs font-bold uppercase tracking-wider text-amber-800 hover:text-amber-900 px-2 py-2"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        <label className="flex flex-col gap-2 md:col-span-2 mb-4 bg-orange-50/50 p-4 border border-orange-100 rounded-lg">
          <span className="text-xs font-bold text-orange-800 uppercase tracking-wider flex items-center gap-2">
            <span className="material-symbols-outlined text-[16px]">domain</span> Issuing Company
          </span>
          <select
            value={issuedCompany}
            onChange={(e) => setIssuedCompany(e.target.value)}
            className="border border-orange-200 bg-white rounded-lg p-3 text-sm font-bold text-secondary focus:border-primary focus:ring-1 outline-none cursor-pointer"
          >
            <option value="WSS Windows & Doors">WSS Windows & Doors (Applies GST)</option>
            <option value="Smart Renovation">Smart Renovation (No GST)</option>
            <option value="Pinki Beans">Pinki Beans (No GST)</option>
          </select>
          <p className="text-[10px] text-outline font-medium">
            Select the corporate entity this quote is officially issued under. This directly alters
            the PDF branding and GST calculations.
          </p>
        </label>

        <PrimaryAddressBlock
          customer={customer}
          errors={errors}
          onChange={handleChange}
          onBlur={handleBlur}
          postalStatus={postalStatus}
          isSearching={isSearching}
        />

        <label className="flex items-center gap-3 md:col-span-2 mt-2 bg-surface-variant/30 p-4 border border-outline-variant/50 rounded-xl cursor-pointer hover:bg-surface-variant/50 transition-colors">
          <div className="relative flex items-center justify-center">
            <input
              type="checkbox"
              checked={hasDifferentInstall}
              onChange={(e) => updateCustomerField('hasDifferentInstall', e.target.checked)}
              className="peer appearance-none w-5 h-5 border-2 border-outline-variant rounded cursor-pointer checked:bg-primary checked:border-primary transition-all shrink-0"
            />
            <span className="material-symbols-outlined absolute text-white text-[16px] pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity">
              check
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-bold text-secondary">
              Different Installation Address?
            </span>
            <span className="text-[10px] text-outline font-medium">
              Check this if the required physical installation address differs from the client
              billing address.
            </span>
          </div>
        </label>

        {hasDifferentInstall && (
          <InstallationAddressBlock
            customer={customer}
            errors={errors}
            onChange={handleChange}
            onBlur={handleBlur}
            installPostalStatus={installPostalStatus}
            isSearchingInstall={isSearchingInstall}
          />
        )}
      </form>
    </section>
  );
}
